import {useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import ReservationPreviewCardContent from "./ReservationPreviewCardContent";
import SaveWithEmailSplitButton from "./SaveWithEmailSplitButton";
import {buildPreviewCardProps} from "../../utils/drop-utils";
import {DragData} from "../../types";
import {useReadStore} from "../../hooks/useReadStore";

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: (sendEmail: boolean) => Promise<void> | void;
    targetTableName: string;
    dragData?: Extract<DragData, { type: "unassigned" | "assigned" }>;
}

/**
 * Asked after every conflict-free floorplan drag & drop: moving a reservation used to save with
 * `allowNotify=true`, so the guest received the "reservation updated" email the moment staff
 * dropped a card. Sending is now an explicit choice — the main action saves silently.
 */
const ConfirmDropEmailDialog: React.FC<Props> = ({
                                                     open,
                                                     onClose,
                                                     onConfirm,
                                                     targetTableName,
                                                     dragData,
                                                 }) => {
    const {t} = useTranslation();
    const floorplanTables = useReadStore(s => s.floorplanTables);
    const [confirming, setConfirming] = useState(false);
    const previewCardProps = buildPreviewCardProps(dragData, targetTableName, floorplanTables);

    // A reopened dialog (next drag) must start from a clean, enabled state.
    useEffect(() => {
        if (open) setConfirming(false);
    }, [open]);

    const handleConfirm = async (sendEmail: boolean) => {
        if (confirming) return;

        setConfirming(true);
        try {
            await onConfirm(sendEmail);
        } finally {
            setConfirming(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={confirming ? undefined : onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                {dragData?.type === "assigned" ? t('Move reservation?') : t('Assign reservation?')}
            </DialogTitle>

            <DialogContent>
                <Box sx={{mb: 2.5}}>
                    <ReservationPreviewCardContent {...previewCardProps} />
                </Box>

                <Alert severity="info" variant="outlined">
                    {t('The guest is only notified when you choose "Save & send email".')}
                </Alert>
            </DialogContent>

            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={confirming}>{t('Cancel')}</Button>
                <SaveWithEmailSplitButton
                    label={t('Save')}
                    loading={confirming}
                    onConfirm={(sendEmail) => void handleConfirm(sendEmail)}
                />
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmDropEmailDialog;
